import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { DashboardChartType } from '@api/models/enums/DashboardChartType';
import { DOMAIN_CONFIG_MAP } from '../analyticsDomainRegistry';
import { AnalyticsMiniChart } from './AnalyticsMiniChart';
import { SortableKpiTile } from './SortableKpiTile';
import { SortableTileWrapper } from './TileDialogStyles';

interface Props {
  tile: DashboardTileDto;
  reduxKey: string;
  onClick: () => void;
  onRetry: () => void;
}

export const SortableTile: FC<Props> = ({ tile, reduxKey, onClick, onRetry }) => {
  const { t } = useTranslation();
  const domainConfig = DOMAIN_CONFIG_MAP[tile.domain];

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const groupByLabelKey = domainConfig?.groupByTitleKeys[tile.groupBy ?? ''];
  // Short group-by label (e.g. "Workplace") for the axis title — the title key above is a full sentence.
  const groupByOption = domainConfig?.groupByOptions.find((o) => o.value === tile.groupBy);
  const groupByAxisLabelKey = groupByOption?.labelKey;

  const isKpi = tile.chartType === DashboardChartType.Kpi;

  return (
    <SortableTileWrapper ref={setNodeRef} style={style} isDragging={isDragging} compact={isKpi} {...attributes} {...listeners}>
      {isKpi ? (
        <SortableKpiTile tile={tile} reduxKey={reduxKey} onClick={onClick} onRetry={onRetry} />
      ) : (
        <AnalyticsMiniChart
          title={tile.title ?? (groupByLabelKey ? t(groupByLabelKey) : undefined)}
          reduxKey={reduxKey}
          groupBy={tile.groupBy ?? undefined}
          onClick={onClick}
          onRetry={onRetry}
          metric={tile.metric ?? undefined}
          xAxisLabel={groupByAxisLabelKey ? t(groupByAxisLabelKey) : undefined}
          sortByValue={groupByOption?.group !== 'time'}
          chartType={tile.chartType}
        />
      )}
    </SortableTileWrapper>
  );
};
